import { DateCalendar } from '@mui/x-date-pickers/DateCalendar'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon'
import { styled } from '@mui/material'
import { PickersDay, PickersDayProps } from '@mui/x-date-pickers'
import { DateTime } from 'luxon'

interface DateRangeCalendarProps {
  range: [DateTime | null, DateTime | null]
  setRange: (dates: [DateTime | null, DateTime | null]) => void
}

interface RangePickersDayProps {
  isInRange?: boolean
  isStart?: boolean
  isEnd?: boolean
}

const RangePickersDay = styled(PickersDay, {
  shouldForwardProp: prop => prop !== 'isInRange' && prop !== 'isStart' && prop !== 'isEnd'
})<RangePickersDayProps>(({ theme, isInRange, isStart, isEnd }) => ({
  ...(isInRange && {
    backgroundColor: theme.palette.primary.light,
    color: theme.palette.primary.contrastText,
    borderRadius: 0,
    '&:hover, &:focus': {
      backgroundColor: theme.palette.primary.main
    },
    margin: 0,
    padding: '0 2px'
  }),
  ...(isStart && {
    backgroundColor: theme.palette.primary.light,
    color: theme.palette.primary.contrastText,
    '&:hover, &:focus': {
      backgroundColor: theme.palette.primary.main
    },
    borderTopLeftRadius: '50%',
    borderBottomLeftRadius: '50%',
    borderTopRightRadius: 0,
    borderBottomRightRadius: 0,
    margin: 0,
    padding: '0 2px'
  }),
  ...(isEnd && {
    backgroundColor: theme.palette.primary.light,
    color: theme.palette.primary.contrastText,
    '&:hover, &:focus': {
      backgroundColor: theme.palette.primary.main
    },
    borderTopRightRadius: '50%',
    borderBottomRightRadius: '50%',
    borderTopLeftRadius: 0,
    borderBottomLeftRadius: 0,
    margin: 0,
    padding: '0 2px'
  })
}))

export function DateRangeCalendar({ range, setRange }: DateRangeCalendarProps) {
  const handleDateChange = (value: unknown) => {
    const dateValue = DateTime.isDateTime(value) ? value : value ? DateTime.fromJSDate(value as Date) : null
    const [start, end] = range

    if (!start || (start && end)) {
      setRange([dateValue, null])
    } else if (dateValue && start && dateValue < start) {
      setRange([dateValue, start])
    } else {
      setRange([start, dateValue])
    }
  }

  const CustomDay = (props: PickersDayProps) => {
    const { day } = props
    const [start, end] = range
    const isInRange = start && end && day > start && day < end
    const isStart = start && day.equals(start)
    const isEnd = end && day.equals(end)

    return (
      <RangePickersDay {...props} isInRange={isInRange ? true : undefined} isStart={isStart ? true : undefined} isEnd={isEnd ? true : undefined} />
    )
  }

  return (
    <LocalizationProvider dateAdapter={AdapterLuxon}>
      <DateCalendar
        value={range[1] || range[0]}
        onChange={handleDateChange}
        disableFuture
        slots={{
          day: CustomDay
        }}
      />
    </LocalizationProvider>
  )
}
